import { useApolloClient, useMutation, useLazyQuery } from "@apollo/client";
import { useEventListener } from "@magento/peregrine";
import { clearCartDataFromCache } from "@magento/peregrine/lib/Apollo/clearCartDataFromCache";
import { clearCustomerDataFromCache } from "@magento/peregrine/lib/Apollo/clearCustomerDataFromCache";
import { useCartContext } from "@magento/peregrine/lib/context/cart";
import { useUserContext } from "@magento/peregrine/lib/context/user";
import { useAwaitQuery } from "@magento/peregrine/lib/hooks/useAwaitQuery";
import { retrieveCartId } from "@magento/peregrine/lib/store/actions/cart";
import SIGNIN_OPERATIONS from "@overrides_venia/peregrine/talons/SignIn/signIn.gql.js";
import mergeOperations from "@magento/peregrine/lib/util/shallowMerge";
import { useCallback } from "react";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import CheckoutPageOperations from "@overrides_venia/venia-ui/components/CheckoutPage/checkoutPage.gql";
import { format } from "date-fns";
import { env } from "build/env";
const getHostFromUrl = value => {
  if (!value || value === "null") {
    return null;
  }
  try {
    return new URL(value).host;
  } catch (_unused) {
    return null;
  }
};
export const useSignInEvent = function () {
  let props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    handleErrors
  } = props;
  const operations = mergeOperations(SIGNIN_OPERATIONS, props.operations);
  const {
    createCartMutation,
    getCustomerQuery,
    mergeCartsMutation
  } = operations;
  const {
    mutations: {
      calculateLSCartMutation,
      setTakeawayDataOnCart
    },
    queries: {
      getCheckoutDetailsQuery
    }
  } = CheckoutPageOperations;
  const apolloClient = useApolloClient();
  const [{
    cartId
  }, {
    createCart,
    removeCart,
    updateCartData
  }] = useCartContext();
  const [{
    isGettingDetails,
    isSignedIn,
    isSigningIn
  }, {
    getUserDetails,
    setToken,
    submitIsSigningIn
  }] = useUserContext();
  const [{
    tableData,
    pickupTime
  }, {
    submitCheckoutStepCompleted
  }] = useCheckoutContext();
  const [setDataOnCart] = useMutation(setTakeawayDataOnCart);
  const [fetchCartId] = useMutation(createCartMutation);
  const [mergeCarts] = useMutation(mergeCartsMutation);
  const [calculateLSCart] = useMutation(calculateLSCartMutation);
  const [getCheckoutDetails] = useLazyQuery(getCheckoutDetailsQuery, {
    fetchPolicy: "no-cache",
    skip: !cartId,
    onError: error => {
      console.log(error);
    }
  });
  const fetchUserDetails = useAwaitQuery(getCustomerQuery);
  const socialLoginEventHandler = useCallback(async event => {
    const backendUrl = getHostFromUrl(env.MAGENTO_BACKEND_URL);
    const originUrl = getHostFromUrl(event.origin);
    if (originUrl !== backendUrl && event.type !== "loginsocial") {
      return null;
    }
    try {
      if (isSignedIn) {
        return null;
      }
      const {
        token,
        result,
        messages,
        redirect_data
      } = (event.type === "loginsocial" ? event.detail : event.data) || {};
      if (result === 1 && token) {
        submitIsSigningIn(true);
        const sourceCartId = cartId;
        await setToken(token);

        // Clear all cart/customer data from cache and redux.
        await clearCartDataFromCache(apolloClient);
        await clearCustomerDataFromCache(apolloClient);
        await removeCart();
        getUserDetails({
          fetchUserDetails
        });
        // Create and get the customer's cart id.
        submitCheckoutStepCompleted({
          pickupTime: false,
          shippingAddress: false,
          payment: false,
          review: false,
          tableData: false,
          shippingMethod: false,
          shippingDataSet: false
        });
        await createCart({
          fetchCartId
        });
        const destinationCartId = await retrieveCartId();

        // Merge the guest cart into the customer cart.
        await mergeCarts({
          variables: {
            destinationCartId,
            sourceCartId
          }
        });
        if (tableData !== null && tableData !== void 0 && tableData.store) {
          var _tableData$store;
          const selectedPickupDateTime = pickupTime !== null && pickupTime !== void 0 && pickupTime.date && pickupTime !== null && pickupTime !== void 0 && pickupTime.hour && typeof (pickupTime === null || pickupTime === void 0 ? void 0 : pickupTime.hour) === "string" ? pickupTime.date + "T" + pickupTime.hour + ":00" : null;
          const currentISOTimestampAdjString = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
          await setDataOnCart({
            variables: {
              cart_id: destinationCartId,
              pickup_date: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
              pickup_time: selectedPickupDateTime ? selectedPickupDateTime : currentISOTimestampAdjString,
              ls_store_id: (_tableData$store = tableData.store) === null || _tableData$store === void 0 ? void 0 : _tableData$store.id,
              ls_covers_quantity: 0,
              ls_sales_type: tableData.sales_type,
              ls_table_number: tableData === null || tableData === void 0 ? void 0 : tableData.table
            }
          });
          await calculateLSCart({
            variables: {
              cartId: destinationCartId
            }
          });
        }
        await getCheckoutDetails({
          variables: {
            cartId: destinationCartId
          },
          onCompleted: data => {
            updateCartData(data.cart);
            submitIsSigningIn(false);
          }
        });
        try {
          var _UIkit$offcanvas, _UIkit$offcanvas2;
          (_UIkit$offcanvas = UIkit.offcanvas("#offcanvas-login-dx")) === null || _UIkit$offcanvas === void 0 ? void 0 : _UIkit$offcanvas.hide();
          (_UIkit$offcanvas2 = UIkit.offcanvas("#offcanvas-registration-dx")) === null || _UIkit$offcanvas2 === void 0 ? void 0 : _UIkit$offcanvas2.hide();
        } catch (error) {
          console.error(error);
        }
      } else if (result === 0) {
        console.error("social-login-error", messages, redirect_data);
      }
    } catch (error) {
      console.error(error);
      submitIsSigningIn(false);
    }
  }, [cartId, apolloClient, removeCart, setToken, createCart, fetchCartId, mergeCarts, getUserDetails, fetchUserDetails, isSignedIn, tableData, pickupTime, handleErrors]);
  const bc = new BroadcastChannel("magento_social_login");
  useEventListener(window, "message", socialLoginEventHandler);
  useEventListener(window, "loginsocial", socialLoginEventHandler);
  useEventListener(bc, "message", socialLoginEventHandler);
  return {
    isBusy: isGettingDetails || isSigningIn,
    isGettingDetails: isGettingDetails
  };
};